import { isTaskCommand } from "@kwp/domain";
import { useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { Button, cx } from "../../ui";
import { IconButton } from "../../ui/icons";
import { AttachButton, AttachmentTray, imageDropProps, pasteImages, useAttachments } from "./images";
import { applyMention, handleFor, matchMembers, mentionQuery } from "./mentions";
import { displayName, type Member } from "./model";

/**
 * The message box: Enter sends, Shift+Enter is a new line, `@` offers members (arrow keys, then
 * Enter or Tab; Escape closes the list), and a leading `/` explains `/task`. With `images`, pictures
 * can be added with the button, by pasting or by dropping them on the box (WP-058).
 */
export function Composer({
  label,
  members,
  onSend,
  initial = "",
  submitLabel = "Send",
  onCancel,
  images = false,
}: {
  label: string;
  members: readonly Member[];
  onSend: (body: string, files: File[]) => void;
  initial?: string;
  submitLabel?: string;
  onCancel?: () => void;
  images?: boolean;
}) {
  const [text, setText] = useState(initial);
  const [caret, setCaret] = useState(initial.length);
  const [active, setActive] = useState(0);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const nextCaret = useRef<number | null>(null);
  const listId = useId();
  const hintId = useId();
  const attachments = useAttachments();

  const query = mentionQuery(text, caret);
  const suggestions = query === null ? [] : matchMembers(members, query.query);
  const open = query !== null && suggestions.length > 0 && dismissedAt !== query.start;
  const activeIndex = Math.min(active, Math.max(suggestions.length - 1, 0));

  useLayoutEffect(() => {
    if (nextCaret.current === null || area.current === null) return;
    area.current.setSelectionRange(nextCaret.current, nextCaret.current);
    nextCaret.current = null;
  });

  function choose(member: Member) {
    if (query === null) return;
    const handle = handleFor(member, members);
    if (handle === null) return;
    const next = applyMention(text, query.start, caret, handle);
    setText(next.text);
    setCaret(next.caret);
    nextCaret.current = next.caret;
    setActive(0);
  }

  function send() {
    const body = text.trim();
    const files = attachments.files;
    if (body.length === 0 && files.length === 0) return;
    if (files.length > 0 && isTaskCommand(body)) {
      attachments.fail("A /task cannot carry images. Send the images in their own message.");
      return;
    }
    onSend(body, files);
    attachments.clear();
    setText("");
    setCaret(0);
    setDismissedAt(null);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.nativeEvent.isComposing) return;
    if (open) {
      const chosen = suggestions[activeIndex];
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const step = e.key === "ArrowDown" ? 1 : -1;
        setActive((activeIndex + step + suggestions.length) % suggestions.length);
        return;
      }
      if ((e.key === "Enter" || e.key === "Tab") && !e.shiftKey && chosen !== undefined) {
        e.preventDefault();
        choose(chosen);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setDismissedAt(query.start);
        return;
      }
    }
    if (e.key === "Escape" && onCancel !== undefined) {
      e.preventDefault();
      onCancel();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  const slash = text.startsWith("/");

  return (
    <div className="relative flex flex-col gap-1">
      {open ? <MentionList id={listId} members={members} suggestions={suggestions} activeIndex={activeIndex} onChoose={choose} /> : null}
      <AttachmentTray files={attachments.files} onRemove={attachments.remove} />
      <div
        className={cx("flex items-end gap-2", onCancel === undefined && "rounded-xl border border-border bg-surface py-1 pl-1 pr-1.5")}
        {...(images ? imageDropProps(attachments.add) : {})}
      >
        {images ? <AttachButton onFiles={attachments.add} /> : null}
        <textarea
          ref={area}
          aria-label={label}
          aria-describedby={hintId}
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={open ? `${listId}-${activeIndex}` : undefined}
          rows={1}
          value={text}
          placeholder={label}
          onChange={(e) => {
            setText(e.target.value);
            setCaret(e.target.selectionStart);
            setActive(0);
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
          onPaste={images ? (e) => pasteImages(e, attachments.add) : undefined}
          onKeyDown={onKeyDown}
          className={cx(
            "field-sizing-content max-h-40 min-h-10 flex-1 resize-none px-3 py-2 text-sm text-text placeholder:text-muted",
            onCancel === undefined ? "border-0 bg-transparent outline-none" : "rounded-md border border-border bg-surface",
          )}
        />
        <ComposerActions submitLabel={submitLabel} empty={text.trim().length === 0 && attachments.files.length === 0} onSend={send} onCancel={onCancel} />
      </div>
      {attachments.error === null ? null : (
        <p role="alert" className="px-1 text-xs text-danger">
          {attachments.error}
        </p>
      )}
      <p id={hintId} className="px-1 text-xs text-muted/80">
        {slash ? (
          <>
            <code>/task</code> Title @person by Friday #private creates a task (assignee, due date and privacy are optional).
          </>
        ) : (
          <>Enter to send · Shift+Enter new line · @ to mention · /task</>
        )}
      </p>
    </div>
  );
}

function MentionList({
  id,
  members,
  suggestions,
  activeIndex,
  onChoose,
}: {
  id: string;
  members: readonly Member[];
  suggestions: readonly Member[];
  activeIndex: number;
  onChoose: (member: Member) => void;
}) {
  return (
    <ul
      id={id}
      role="listbox"
      aria-label="Mention someone"
      className="absolute bottom-full left-0 z-10 mb-1 w-64 max-w-full overflow-hidden rounded-md border border-border bg-surface shadow-lg"
    >
      {suggestions.map((m, i) => (
        <li
          key={m.uid}
          id={`${id}-${i}`}
          role="option"
          aria-selected={i === activeIndex}
          className={cx("cursor-pointer px-3 py-1.5 text-sm text-text", i === activeIndex && "bg-surface-2")}
          onMouseDown={(e) => {
            e.preventDefault();
            onChoose(m);
          }}
        >
          <span className="font-medium">{displayName(m, m.uid)}</span>
          <span className="text-muted"> @{handleFor(m, members)}</span>
        </li>
      ))}
    </ul>
  );
}

/** A new message sends with an arrow inside the box; an edit has Cancel and its own label. */
function ComposerActions({ submitLabel, empty, onSend, onCancel }: { submitLabel: string; empty: boolean; onSend: () => void; onCancel: (() => void) | undefined }) {
  if (onCancel === undefined) {
    return (
      <IconButton
        icon="send"
        label={submitLabel}
        title={`${submitLabel} (Enter)`}
        tone="primary"
        className="mb-0.5"
        onClick={onSend}
        disabled={empty}
      />
    );
  }
  return (
    <>
      <Button onClick={onCancel}>Cancel</Button>
      <Button variant="primary" onClick={onSend} disabled={empty}>
        {submitLabel}
      </Button>
    </>
  );
}
